import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import SeoHead from "@/components/SeoHead";

const TermsOfServicePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50" data-testid="terms-page">
      <SeoHead title="Terms of Service" description="Read the Terms of Service for PublicBeachAccess.com. These terms govern your use of our public beach directory, including data accuracy disclaimers, user responsibilities, and limitations of liability." path="/terms" />
      {/* Header */}
      <div className="bg-[#0EA5E9] text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/">
            <Button variant="ghost" className="text-white hover:bg-white/20 mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-3xl sm:text-4xl font-extrabold font-['Outfit']">
            Terms of Service
          </h1>
          <p className="text-white/80 mt-2">Last Updated: April 1, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-8">
              Welcome to PublicBeachAccess.com. By accessing or using our website, you agree to be bound by these Terms of Service. Please read them carefully.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600 mb-6">
              By accessing and using PublicBeachAccess.com ("Website," "Service"), operated by Digital Properties, LLC ("we," "our," or "us"), you accept and agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use our Website.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">2. Description of Service</h2>
            <p className="text-gray-600 mb-6">
              PublicBeachAccess.com provides a free online directory of public beach access points across the United States. Our Service includes information about beach locations, amenities, directions, and related content for informational purposes only.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">3. Use of the Website</h2>
            <p className="text-gray-600 mb-4">You agree to use our Website only for lawful purposes and in accordance with these Terms. You agree not to:</p>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-1">
              <li>Use the Website in any way that violates applicable laws or regulations</li>
              <li>Attempt to gain unauthorized access to any part of the Website</li>
              <li>Use the Website to transmit harmful code or malware</li>
              <li>Scrape, copy, or republish our content without permission</li>
              <li>Use automated systems to access the Website in a manner that sends more requests than a human can reasonably produce</li>
              <li>Interfere with or disrupt the Website's functionality</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">4. Intellectual Property</h2>
            <p className="text-gray-600 mb-6">
              All content on this Website, including but not limited to text, graphics, logos, icons, images, and software, is the property of Digital Properties, LLC or its content suppliers and is protected by intellectual property laws. You may not reproduce, distribute, modify, or create derivative works without our prior written consent.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">5. Disclaimer of Warranties</h2>
            <p className="text-gray-600 mb-6">
              THE WEBSITE AND ALL CONTENT ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. We do not warrant that the Website will be uninterrupted, error-free, or free of viruses or other harmful components.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">6. Limitation of Liability</h2>
            <p className="text-gray-600 mb-6">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, PUBLICBEACHACCESS.COM SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE WEBSITE, REGARDLESS OF WHETHER SUCH DAMAGES ARE BASED ON WARRANTY, CONTRACT, TORT, OR ANY OTHER LEGAL THEORY.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">7. Indemnification</h2>
            <p className="text-gray-600 mb-6">
              You agree to indemnify, defend, and hold harmless Digital Properties, LLC and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising out of or related to your use of the Website or violation of these Terms.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">8. Third-Party Links</h2>
            <p className="text-gray-600 mb-6">
              Our Website may contain links to third-party websites or services. We are not responsible for the content, privacy policies, or practices of any third-party sites. Your use of third-party websites is at your own risk.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">9. Modifications to Terms</h2>
            <p className="text-gray-600 mb-6">
              We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to the Website. Your continued use of the Website after any changes constitutes acceptance of the new Terms.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">10. Termination</h2>
            <p className="text-gray-600 mb-6">
              We reserve the right to terminate or suspend your access to the Website at any time, without prior notice or liability, for any reason, including breach of these Terms.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">11. Governing Law</h2>
            <p className="text-gray-600 mb-6">
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
            </p>

            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mt-8 mb-4">12. Contact Information</h2>
            <p className="text-gray-600 mb-6">
              If you have any questions about these Terms of Service, please contact us at{" "}
              <a href="mailto:hello.digitalproperties@gmail.com" className="text-[#0EA5E9] hover:underline">hello.digitalproperties@gmail.com</a>{" "}
              or through our <Link to="/contact" className="text-[#0EA5E9] hover:underline">Contact Page</Link>.
            </p>

            <div className="bg-[#FEF3C7] rounded-xl p-6 mt-8">
              <p className="text-gray-700 font-medium">
                By using PublicBeachAccess.com, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
